/**
 * ADR-0016 Phase 3 residual — prefer frozen / checkout-captured COGS over re-FIFO.
 *
 * Truth order for a sale COGS:
 * 1. Fully frozen (inventoryCostFrozen / costSnapshot.frozen / restaurant flags)
 * 2. inventoryCost + checkout usedLots evidence (legacy/pre-freeze checkout trail)
 * 3. Sum of line extendedCost / inventoryCost when present
 * 4. null → caller may use usedLots line math, then live-lot FIFO last
 */

import { isSaleFullyCancelled, getSaleLineActiveQuantity } from '../sales/saleLineMath';
import { isRestaurantScopedSale } from '../restaurant/restaurantContext';

/**
 * @param {object} sale
 * @returns {boolean}
 */
export function isSaleCostFrozen(sale) {
  if (!sale || typeof sale !== 'object') return false;
  return (
    sale.inventoryCostFrozen === true ||
    sale.costSnapshot?.frozen === true ||
    sale.restaurantInventoryCost === true ||
    isRestaurantScopedSale(sale)
  );
}

/**
 * Checkout captured lot consumption lines — trustworthy for COGS even if frozen flag missing.
 * @param {object} sale
 */
export function saleHasUsedLotsCapture(sale) {
  const products = Array.isArray(sale?.products) ? sale.products : [];
  return products.some(
    (p) => Array.isArray(p?.usedLots) && p.usedLots.some((u) => (Number(u?.quantity) || 0) > 0)
  );
}

/**
 * Sum line COGS when each active line carries frozen extendedCost.
 * @param {object} sale
 * @returns {number|null}
 */
export function resolveSaleLineExtendedCogs(sale) {
  const products = Array.isArray(sale?.products) ? sale.products : [];
  if (!products.length) return null;
  let total = 0;
  let any = false;
  for (const line of products) {
    const active = getSaleLineActiveQuantity(line);
    if (!(active > 0)) continue;
    const ext =
      line.extendedCost != null
        ? Number(line.extendedCost)
        : line.inventoryCost != null
          ? Number(line.inventoryCost)
          : line.lineCost != null
            ? Number(line.lineCost)
            : null;
    if (ext == null || !Number.isFinite(ext)) return null;
    // If line has full-qty extendedCost, scale for partial void
    const fullQty = Number(line.quantity) || active;
    const scale = fullQty > 0 ? Math.min(1, active / fullQty) : 1;
    total += ext * scale;
    any = true;
  }
  return any ? total : null;
}

/**
 * @param {object} sale
 * @returns {number|null} COGS for this sale, or null to fall back to per-line FIFO
 */
export function resolveSaleInventoryCogs(sale) {
  if (!sale || isSaleFullyCancelled(sale)) return 0;

  // Explicit snapshot wins
  if (sale.costSnapshot?.frozen === true && sale.costSnapshot.inventoryCost != null) {
    const c = Number(sale.costSnapshot.inventoryCost);
    if (Number.isFinite(c)) return c;
  }

  if (isSaleCostFrozen(sale) && sale.inventoryCost != null && sale.inventoryCost !== '') {
    const cost = Number(sale.inventoryCost);
    if (Number.isFinite(cost)) return cost;
  }

  // Legacy checkout with usedLots trail + inventoryCost (not stray)
  if (
    sale.inventoryCost != null &&
    sale.inventoryCost !== '' &&
    saleHasUsedLotsCapture(sale)
  ) {
    const cost = Number(sale.inventoryCost);
    if (Number.isFinite(cost)) return cost;
  }

  // Line-level frozen extended costs
  const fromLines = resolveSaleLineExtendedCogs(sale);
  if (fromLines != null) return fromLines;

  return null;
}

/**
 * In-memory soft-freeze for export/migration: attaches flags when COGS can be trusted.
 * Does not persist by itself — caller writes sales array.
 *
 * @param {object[]} sales
 * @returns {{ sales: object[], frozenNow: number, alreadyFrozen: number, skipped: number }}
 */
export function softFreezeSalesFromCaptures(sales = []) {
  let frozenNow = 0;
  let alreadyFrozen = 0;
  let skipped = 0;
  const next = (Array.isArray(sales) ? sales : []).map((sale) => {
    if (!sale || typeof sale !== 'object') {
      skipped += 1;
      return sale;
    }
    if (isSaleFullyCancelled(sale)) return sale;
    if (isSaleCostFrozen(sale) && sale.inventoryCost != null) {
      alreadyFrozen += 1;
      return sale;
    }
    const cogs = resolveSaleInventoryCogs(sale);
    if (cogs == null) {
      // try force from usedLots only via inventoryCost missing
      if (!saleHasUsedLotsCapture(sale)) {
        skipped += 1;
        return sale;
      }
    }
    const cost =
      cogs != null
        ? cogs
        : Number(sale.inventoryCost);
    if (!Number.isFinite(cost) && cogs == null) {
      skipped += 1;
      return sale;
    }
    const finalCost = Number.isFinite(cost) ? cost : 0;
    frozenNow += 1;
    return {
      ...sale,
      inventoryCost: finalCost,
      inventoryCostFrozen: true,
      costSnapshot: {
        ...(sale.costSnapshot && typeof sale.costSnapshot === 'object' ? sale.costSnapshot : {}),
        frozen: true,
        inventoryCost: finalCost,
        extendedCost: finalCost,
        capturedAt: sale.costSnapshot?.capturedAt || new Date().toISOString(),
        source: sale.costSnapshot?.source || 'soft_freeze_used_lots'
      }
    };
  });
  return { sales: next, frozenNow, alreadyFrozen, skipped };
}

/**
 * Sum period COGS preferring frozen sale facts (ADR-0016 Phase 3).
 * Does not compute FIFO — call when only frozen cost rows matter or for diagnostics.
 *
 * @param {Array} sales
 * @returns {{ total: number, frozenCount: number, unfrozenCount: number, cancelledCount: number }}
 */
export function sumFrozenSalesCogs(sales = []) {
  let total = 0;
  let frozenCount = 0;
  let unfrozenCount = 0;
  let cancelledCount = 0;
  for (const sale of sales || []) {
    if (isSaleFullyCancelled(sale)) {
      cancelledCount += 1;
      continue;
    }
    const cogs = resolveSaleInventoryCogs(sale);
    if (cogs != null) {
      total += cogs;
      frozenCount += 1;
    } else {
      unfrozenCount += 1;
    }
  }
  return { total, frozenCount, unfrozenCount, cancelledCount };
}

/**
 * @param {Array} consumptions
 * @returns {number}
 */
export function sumStockConsumptionCosts(consumptions = []) {
  return (consumptions || []).reduce((sum, c) => sum + (Number(c?.totalCost) || 0), 0);
}

/**
 * @param {object} consumption
 * @returns {Date|null}
 */
export function consumptionDateValue(consumption) {
  const raw = consumption?.date || consumption?.createdAt;
  if (!raw) return null;
  const d = new Date(String(raw).includes('T') ? raw : `${raw}T12:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}
